import { Box } from '@mui/material';

import type { KeyboardEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NoDataMessage } from '@/shared/ui';

import { TABLE_ROW_HEIGHT_PX } from '@/constants/constants';
import useDateText, { DateTextVariants } from '@/hooks/useDateText';
import { EmptyState } from '@/shared/ui/feedback/EmptyState';
import type { SpotHistoryTableColumnType, SpotHistoryTableRowType } from '@/types/markets';

import { DATA_COLUMN_WIDTH } from './constants';
import TableBody from './TableBody';
import TableHeaderRow from './TableHeaderRow';
import type { DisplayRow } from './types';
import { useColumnWidths } from './useColumnWidths';
import { useSpotHistoryVirtualizers } from './useSpotHistoryVirtualizers';
import { getColumnCode } from './utils';

type SpotHistoryTableProps = {
  columns: SpotHistoryTableColumnType[];
  rows: SpotHistoryTableRowType[];
  layout?: { fullWidth?: boolean; height?: number | string } | Record<string, string>;
};

function resolveMaxHeight(
  layoutHeight: number | string | boolean | undefined,
  displayRowCount: number,
  headerHeight: number
) {
  const layoutRows = Number(layoutHeight);
  const visibleRows =
    Number.isFinite(layoutRows) && layoutRows > 0 ? layoutRows : Math.min(displayRowCount, 20);
  return visibleRows * TABLE_ROW_HEIGHT_PX + headerHeight;
}

const SpotHistoryTable = ({ columns, rows, layout }: SpotHistoryTableProps) => {
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);
  const suppressNextHeaderToggleRef = useRef(false);
  const clearSuppressHeaderToggleTimeoutRef = useRef<number | undefined>(undefined);
  const { columnWidths, setColumnWidth } = useColumnWidths();
  const { formatDateString } = useDateText();

  const [measuredHeaderHeight, setMeasuredHeaderHeight] = useState(0);
  const effectiveHeaderHeight = measuredHeaderHeight;

  const dataColumnWidths = useMemo(
    () => columns.map(column => columnWidths[getColumnCode(column)] ?? DATA_COLUMN_WIDTH),
    [columnWidths, columns]
  );

  const displayRows = useMemo<DisplayRow[]>(
    () =>
      rows.map(row => ({
        ...row,
        formattedDate: formatDateString(row.date, DateTextVariants.FULL)
      })),
    [formatDateString, rows]
  );

  const maxHeight = useMemo(
    () => resolveMaxHeight(layout?.height, displayRows.length, effectiveHeaderHeight),
    [displayRows.length, effectiveHeaderHeight, layout?.height]
  );

  const { scrollRef, virtualColumns, virtualRows, totalWidth, bodyHeight } =
    useSpotHistoryVirtualizers({
      columnCount: columns.length,
      displayRowCount: displayRows.length,
      dataColumnWidths,
      headerHeight: effectiveHeaderHeight,
      maxHeight
    });

  useEffect(
    () => () => {
      if (clearSuppressHeaderToggleTimeoutRef.current !== undefined) {
        window.clearTimeout(clearSuppressHeaderToggleTimeoutRef.current);
      }
    },
    []
  );

  const toggleHeader = useCallback(() => {
    if (suppressNextHeaderToggleRef.current) {
      suppressNextHeaderToggleRef.current = false;
      if (clearSuppressHeaderToggleTimeoutRef.current !== undefined) {
        window.clearTimeout(clearSuppressHeaderToggleTimeoutRef.current);
        clearSuppressHeaderToggleTimeoutRef.current = undefined;
      }
      return;
    }
    setIsHeaderExpanded(prev => !prev);
  }, []);

  const handleResizeEnd = useCallback((shouldSuppressClick: boolean) => {
    if (!shouldSuppressClick) return;

    suppressNextHeaderToggleRef.current = true;
    if (clearSuppressHeaderToggleTimeoutRef.current !== undefined) {
      window.clearTimeout(clearSuppressHeaderToggleTimeoutRef.current);
    }
    clearSuppressHeaderToggleTimeoutRef.current = window.setTimeout(() => {
      suppressNextHeaderToggleRef.current = false;
      clearSuppressHeaderToggleTimeoutRef.current = undefined;
    });
  }, []);

  const handleHeaderKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      toggleHeader();
    },
    [toggleHeader]
  );

  if (!columns.length) {
    return <EmptyState />;
  }

  if (!displayRows.length) {
    return <NoDataMessage />;
  }

  return (
    <Box
      ref={scrollRef}
      aria-colcount={columns.length + 1}
      aria-label='spot history table'
      aria-rowcount={displayRows.length + 1}
      role='table'
      sx={{
        maxHeight,
        overflow: 'auto',
        pb: 2,
        position: 'relative',
        width: '100%'
      }}
    >
      <Box
        sx={{
          height: effectiveHeaderHeight + bodyHeight,
          minWidth: totalWidth,
          position: 'relative'
        }}
      >
        <TableHeaderRow
          columns={columns}
          virtualColumns={virtualColumns}
          dataColumnWidths={dataColumnWidths}
          totalWidth={totalWidth}
          isHeaderExpanded={isHeaderExpanded}
          scrollRef={scrollRef}
          setColumnWidth={setColumnWidth}
          onToggle={toggleHeader}
          onToggleKeyDown={handleHeaderKeyDown}
          onResizeEnd={handleResizeEnd}
          onHeightChange={setMeasuredHeaderHeight}
        />
        <TableBody
          columns={columns}
          displayRows={displayRows}
          virtualColumns={virtualColumns}
          virtualRows={virtualRows}
          bodyHeight={bodyHeight}
          totalWidth={totalWidth}
        />
      </Box>
    </Box>
  );
};

export default SpotHistoryTable;
